/**
 * Streaming Custom Routes (Server-Sent Events)
 *
 * `useResourceStream` is the streaming counterpart of `useResourceCustomMutation`:
 * the same resource + path template + method, the same `{ params, body }`
 * variables, the same base URL, Clerk token, and error shaping — but the
 * response is read as a Server-Sent Events stream and handed back frame by
 * frame, so a surface can render a long answer while the server is still
 * writing it.
 *
 * Frames follow the framework's `sse()` helper: `event:` names the frame and
 * `data:` carries JSON. Data that is not JSON is passed through as a string.
 *
 * @example
 * ```tsx
 * const streamDraft = useResourceStream<{ hint: string }, { orderId: string }>(
 *   "orders",
 *   "/:orderId/compose-message",
 * );
 * for await (const frame of streamDraft({ params: { orderId }, body: { hint } }, { signal })) {
 *   if (frame.event === "delta") append(frame.data);
 * }
 * ```
 */

import { useCallback } from "react";
import { useSessionToken } from "./use-session-token";
import { getClientConfig } from "../types";
import {
  CustomApiError,
  type CustomMutationVariables,
  readApiError,
  resolvePathParams,
  splitMutationVariables,
} from "./use-resource-custom";
import { buildRequestHeaders } from "./request-headers";

// ============================================================================
// Types
// ============================================================================

/** One Server-Sent Event, with its `data:` already JSON-decoded when it was JSON. */
export interface SseFrame<TData = unknown> {
  /** The `event:` field; "message" when the server sent none. */
  event: string;
  data: TData;
  /** The `id:` field, when the server sent one. */
  id?: string;
}

export interface StreamRequestOptions {
  /** Aborts the request and closes the stream — wire the surface's cancel to it. */
  signal?: AbortSignal;
}

/** What `useResourceStream` returns: open the stream for one set of variables. */
export type StreamRequest<TBody, TParams> = (
  variables: CustomMutationVariables<TBody, TParams>,
  options?: StreamRequestOptions,
) => AsyncGenerator<SseFrame>;

// ============================================================================
// SSE Parsing
// ============================================================================

/** The blank line that ends an SSE frame, with either line ending. */
const FRAME_SEPARATOR = /\r?\n\r?\n/;

/**
 * Parse one SSE block — the text between two blank lines — into a frame.
 * Comment lines (`:`) are skipped; several `data:` lines join with newlines,
 * per the spec. A block with no data (a keep-alive comment) yields `null`.
 * Exported for testing — callers read frames through `useResourceStream`.
 */
export function parseSseBlock(block: string): SseFrame | null {
  let event = "message";
  let id: string | undefined;
  const dataLines: string[] = [];

  for (const line of block.split(/\r?\n/)) {
    if (line.length === 0 || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    const rawValue = colon === -1 ? "" : line.slice(colon + 1);
    const value = rawValue.startsWith(" ") ? rawValue.slice(1) : rawValue;
    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
    else if (field === "id") id = value;
  }

  if (dataLines.length === 0) return null;
  const raw = dataLines.join("\n");
  let data: unknown = raw;
  try {
    data = JSON.parse(raw);
  } catch {
    // Not JSON — the text itself is the payload.
  }
  return id === undefined ? { event, data } : { event, data, id };
}

/**
 * Split a byte stream into SSE frames as each one completes. Chunk boundaries
 * fall anywhere — mid-line, mid-multibyte character — so bytes are buffered
 * until a frame separator arrives.
 */
export async function* readSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });

      let separator = FRAME_SEPARATOR.exec(buffer);
      while (separator) {
        const frame = parseSseBlock(buffer.slice(0, separator.index));
        buffer = buffer.slice(separator.index + separator[0].length);
        if (frame) yield frame;
        separator = FRAME_SEPARATOR.exec(buffer);
      }

      if (done) {
        const last = buffer.trim().length > 0 ? parseSseBlock(buffer) : null;
        if (last) yield last;
        return;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ============================================================================
// Transport
// ============================================================================

async function* streamCustom<TBody>(
  getToken: () => Promise<string | null>,
  resource: string,
  path: string,
  method: "POST" | "PUT" | "PATCH",
  variables: unknown,
  options: StreamRequestOptions | undefined,
): AsyncGenerator<SseFrame> {
  const config = getClientConfig();
  const { params, body, headers } = splitMutationVariables<TBody>(
    path,
    variables,
  );
  const token = await getToken();
  const resolvedPath = resolvePathParams(path, params);
  // Avoid trailing slash when path is "/" (causes CORS issues with Next.js redirects)
  const pathSuffix = resolvedPath === "/" ? "" : resolvedPath;
  const url = `${config.baseUrl}/${resource}${pathSuffix}`;

  const response = await fetch(url, {
    method,
    headers: {
      ...buildRequestHeaders(token, headers),
      Accept: "text/event-stream",
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(options?.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok) throw await readApiError(response);
  if (!response.body) {
    throw new CustomApiError(
      {
        error: {
          code: "EMPTY_STREAM",
          message: "The server returned no stream",
        },
      },
      response.status,
    );
  }

  yield* readSseStream(response.body);
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Open a streaming custom route.
 *
 * Returns a stable function; each call opens one request and yields its SSE
 * frames until the server closes the stream, the consumer stops iterating, or
 * `options.signal` aborts. A non-2xx status throws `CustomApiError` before the
 * first frame, exactly like the JSON hooks.
 *
 * @template TBody - Request body type
 * @template TParams - Path parameters type (routes with `:param` tokens take a `{ params, body }` envelope)
 */
export function useResourceStream<
  TBody,
  TParams extends Record<string, string> = never,
>(
  resource: string,
  path: string,
  method: "POST" | "PUT" | "PATCH" = "POST",
): StreamRequest<TBody, TParams> {
  const getToken = useSessionToken();

  return useCallback(
    (
      variables: CustomMutationVariables<TBody, TParams>,
      options?: StreamRequestOptions,
    ) =>
      streamCustom<TBody>(getToken, resource, path, method, variables, options),
    [getToken, resource, path, method],
  );
}
